import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { MissingCrateFilterParams, MissingCrateSortParams } from '../../../core/services/missing-crates.service';
import * as moment from 'moment';

@Component({
  selector: 'app-unknown-address-table-header',
  templateUrl: './unknown-address-table-header.component.html',
  styleUrls: ['./unknown-address-table-header.component.scss']
})
export class UnknownAddressTableHeaderComponent implements OnInit {
  @Output() filterValues = new EventEmitter();
  @Output() onSelectAll = new EventEmitter(false);
  @Input() allSelected: boolean;
  showCheckIcon = {};
  sortDirection: "ASC" | "DESC";
  sortParams: MissingCrateSortParams;
  appliedFilters = {};
  sortColumn = '';
  filterParams: MissingCrateFilterParams;
  maxDate = new Date();
  minDate: Date;
  lastMovedAtMinDate: Date;
  lastMovedAtMaxDate = new Date();
  form = new FormGroup({
    startDate: new FormControl(null),
    endDate: new FormControl(null),
    sku: new FormControl(null),
    tagRfid: new FormControl(null),
    minActiveInventoryCount: new FormControl(null),
    maxActiveInventoryCount: new FormControl(null),
    activeInventoryType: new FormControl(null),
    quantity: new FormControl(null),
    inventoryCount: new FormControl(null),
    section: new FormControl(null),
    alias: new FormControl(null),
    lastMovedAtStartDate: new FormControl(null),
    lastMovedAtEndDate: new FormControl(null)
  });

  constructor() { }

  get isReceivedAtSaveBtnDisabled(): boolean {
    const start = this.form.get('startDate');
    const end = this.form.get('endDate');
  
    const bothDatesValid = start?.valid && end?.valid;
    const bothDatesEmpty = !start?.value && !end?.value;
  
    if (this.showCheckIcon['receivedAt']) {
      return !(bothDatesValid || bothDatesEmpty);
    } else {
      return !bothDatesValid;
    }
  }

  get isLastMovedAtSaveBtnDisabled(): boolean {
    const start = this.form.get('lastMovedAtStartDate');
    const end = this.form.get('lastMovedAtEndDate');
  
    const bothDatesValid = start?.valid && end?.valid;
    const bothDatesEmpty = !start?.value && !end?.value;
  
    if (this.showCheckIcon['lastMovedAt']) {
      return !(bothDatesValid || bothDatesEmpty);
    } else {
      return !bothDatesValid;
    }
  }

  ngOnInit(): void {
  }

  sort(orderBy: MissingCrateSortParams['orderBy'], sortBy: "ASC" | "DESC") {
    this.sortParams = null;
    this.showCheckIcon = {};
    this.sortDirection = sortBy;
    this.showCheckIcon[orderBy] = true;
    this.sortParams = {
      orderBy: orderBy,
      sortBy: sortBy
    };
  }

  clearFilters(menu: MatMenuTrigger, filter: string) {
    this.maxDate = new Date();
    this.minDate = null;
    this.lastMovedAtMinDate = null;
    this.lastMovedAtMaxDate = new Date();
    this.sortColumn = this.sortColumn === filter ? null : this.sortColumn;
    this.sortParams = this.sortParams?.orderBy === filter ? null : this.sortParams;
    delete this.appliedFilters?.[filter];
    delete this.showCheckIcon?.[filter];

    switch (filter) {
      case 'activeInventoryCount':
        this.form.get('minActiveInventoryCount').reset();
        this.form.get('maxActiveInventoryCount').reset();
        this.form.get('activeInventoryType').reset();
        delete this.filterParams?.['maxActiveInventoryCount'];
        delete this.filterParams?.['minActiveInventoryCount'];
        break;
      case 'inventoryCount':
        this.form.get('inventoryCount').reset();
        delete this.filterParams?.['inventoryCount'];
        break
      case 'tagRfid':
        this.form.get('tagRfid').reset();
        delete this.filterParams?.['tagRfid'];
        break;
      case 'quantity':
        this.form.get('quantity').reset();
        delete this.filterParams?.['quantity'];
        break;
      case 'receivedAt':
        this.form.get('startDate').reset();
        this.form.get('endDate').reset();
        delete this.filterParams?.['receivedAtStartDate'];
        delete this.filterParams?.['receivedAtEndDate'];
        break;
      case 'sku':
        this.form.get('sku').reset();
        delete this.filterParams?.['sku'];
        break;
      case 'section':
        this.form.get('section').reset();
        delete this.filterParams?.['section'];
        break;;
      case 'alias':
        this.form.get('alias').reset();
        delete this.filterParams?.['alias'];
        break;
      case 'lastMovedAt':
        this.form.get('lastMovedAtStartDate').reset();
        this.form.get('lastMovedAtEndDate').reset();
        delete this.filterParams?.['lastMovedAtStartDate'];
        delete this.filterParams?.['lastMovedAtEndDate'];
        break;
    }

    this.filterValues.emit({
      filterParams: this.filterParams,
      sortParams: this.sortParams
    });
    menu.closeMenu();
  }

  applyFilters(menu: MatMenuTrigger) {
    const startDate = this.form.value.startDate ? moment(this.form.value.startDate._d).valueOf() : null;
    const endDate = this.form.value.endDate ? moment(this.form.value.endDate._d).endOf('day').valueOf() : null;
    const startDateMovedAt = this.form.value.lastMovedAtStartDate ? moment(this.form.value.lastMovedAtStartDate._d).valueOf() : null;
    const endDateMovedAt = this.form.value.lastMovedAtEndDate ? moment(this.form.value.lastMovedAtEndDate._d).endOf('day').valueOf() : null;

    const params = {
      receivedAtStartDate: startDate,
      receivedAtEndDate: endDate,
      lastMovedAtStartDate: startDateMovedAt,
      lastMovedAtEndDate: endDateMovedAt,
      quantity: this.form.value.quantity,
      inventoryCount: this.form.value.inventoryCount,
      maxActiveInventoryCount: this.form.value.maxActiveInventoryCount,
      minActiveInventoryCount: this.form.value.minActiveInventoryCount,
      sku: this.form.value.sku,
      tagRfid: this.form.value.tagRfid,
      alias: this.form.value.alias,
      section: this.form.value.section
    };

    this.removeNullKeys(params);
    this.sortColumn = this.sortParams?.orderBy;
    this.filterValues.emit({
      filterParams: params,
      sortParams: this.sortParams
    });
    menu.closeMenu();
  }

  removeNullKeys(params) {
    Object.keys(params).forEach(key => {
      if (params[key] === null) {
        delete params[key];
      } else {
        this.setAppliedFilters(key);
      }
    });
  }

  setAppliedFilters(name) {
    switch (name) {
      case 'receivedAtStartDate':
      case 'receivedAtEndDate':
        this.appliedFilters['receivedAt'] = true;
        break;
      case 'lastMovedAtStartDate':
      case 'lastMovedAtEndDate':
        this.appliedFilters['lastMovedAt'] = true;
        break;
      case 'minActiveInventoryCount':
      case 'maxActiveInventoryCount':
        this.appliedFilters['activeInventoryCount'] = true;
      default:
        this.appliedFilters[name] = true;
        break;
    }
  }

  selectAll() {
    this.onSelectAll.emit(!this.allSelected);
  }

  onActiveInventoryChange(event) {
    const selectedValue = event.value;

    if (selectedValue === 1) {
      this.form.patchValue({
        minActiveInventoryCount: 1,
        maxActiveInventoryCount: null
      });
    } else if (selectedValue === 0) {
      this.form.patchValue({
        minActiveInventoryCount: null,
        maxActiveInventoryCount: 0
      });
    }
  }

  onReceivedAtStartDateChange() {
    this.minDate = this.form.value.startDate._d;
  }

  onReceivedAtEndDateChange() {
    this.maxDate = this.form.value.endDate._d;
  }

  onLastMovedAtStartDateChange() {
    this.lastMovedAtMinDate = this.form.value.lastMovedAtStartDate._d;
  }

  onLastMovedAtEndDateChange() {
    this.lastMovedAtMaxDate = this.form.value.lastMovedAtEndDate._d;
  }

}
